import { FC, useRef } from 'react';
import { useVariableValue } from '@devcycle/react-client-sdk';

import { Popover } from '../Popover';
import { LoginListMenu } from '../LoginListMenu';
import { HEADER } from '../../consts';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import { pushGTMEvent } from '../../utilities';
import { BurgerMenu } from '../BurgerMenu';
import { Button } from '../Button';
import { HeaderProps } from '../Header';
import { Loader } from '../Loader';
import { MobileUserMenu } from '../MobileUserMenu';
import { MyAccountDetails } from '../MyAccountDetails';
import { HeaderData, OpCoContent, useOptionalContent } from '../services/content';
import { UserActions } from '../UserActions';
import { useSafeCookies } from '../../hooks/useCookies/useSafeCookies';
import { useUserDetailsError } from '../../contexts/userDetailsError';
import { useUIString } from '../services/content/hooks/useUIString';

import styles from './UserDetails.module.css';

export interface UserDetailsProps {
  auth: HeaderProps['auth'];
  householdAviosBalance?: HeaderProps['householdAviosBalance'];
  isLoading?: HeaderProps['isLoading'];
  signInLabel: HeaderData['signInLabel'];
  userBalance?: HeaderProps['userBalance'];
  userDetails?: HeaderProps['userDetails'];
}

const renderLoader = (height: number, width1: number, width2: number) => (
  <>
    <Loader height={height} width={width1} />
    <Loader height={height} width={width2} />
  </>
);

export const UserDetails: FC<UserDetailsProps> = ({ auth, householdAviosBalance, isLoading, signInLabel, userBalance, userDetails }) => {
  const isSmallScreenSize = useScreenWidth() < 1024;
  const { userDetailsError } = useUserDetailsError();
  const userDetailsAvailable = !!userDetails && !userDetailsError;
  const { loginProps, logoutProps } = auth;
  const multiOpcoFlag = useVariableValue('multi-opco-login', false);
  const relativeRef = useRef<HTMLSpanElement | null>(null);
  const { data: currentOpco } = useOptionalContent(OpCoContent);
  const signInDropdownAriaLabel = useUIString('signInDropdownAriaLabel');
  const { setOpco } = useSafeCookies();

  const handleLoginClick = (opcoId: string, isOpCoSupported?: boolean) => {
    setOpco(opcoId);
    pushGTMEvent(HEADER, `sign in - ${opcoId.toLowerCase()}`);
    if (isOpCoSupported !== false) {
      loginProps.onClick?.(opcoId);
    }
  };

  const loggedInMobileView = () => (
    <div data-testid="user-details-mobile-loader" className={styles.iconWrapper}>
      {isLoading ? (
        renderLoader(32, 42, 42)
      ) : (
        <>
          <MobileUserMenu
            householdAviosBalance={householdAviosBalance}
            loginProps={loginProps}
            logoutProps={logoutProps}
            userBalance={userBalance}
            userDetails={userDetails!}
          />
          <BurgerMenu isLoggedIn={userDetailsAvailable} loginProps={loginProps} logoutProps={logoutProps} />
        </>
      )}
    </div>
  );

  const loggedOutMobileView = () => (
    <div data-testid="user-details-mobile-loader" className={styles.iconWrapper}>
      {isLoading ? (
        renderLoader(32, 62, 42)
      ) : (
        <>
          {multiOpcoFlag ? (
            <span ref={relativeRef}>
              <Popover
                placement="bottom right"
                closeDelay={600}
                openDelay={300}
                relRef={relativeRef}
                offset={16}
                trigger={
                  <Button size="sm" disableRouting>
                    {signInLabel}
                  </Button>
                }
                aria-label={signInDropdownAriaLabel}
                isNonModal
              >
                <LoginListMenu loginUrl={loginProps.href} onLoginClick={handleLoginClick} />
              </Popover>
            </span>
          ) : (
            <Button disableRouting href={loginProps.href} onPress={() => handleLoginClick(currentOpco!.id)} size="sm">
              {signInLabel}
            </Button>
          )}

          <BurgerMenu isLoggedIn={userDetailsAvailable} loginProps={loginProps} logoutProps={logoutProps} />
        </>
      )}
    </div>
  );

  const loggedInDesktopView = () =>
    isLoading ? (
      renderLoader(48, 92, 62)
    ) : (
      <MyAccountDetails balance={userBalance} householdAviosBalance={householdAviosBalance} logoutProps={logoutProps} name={userDetails!.name} />
    );

  const loggedOutDesktopView = () =>
    isLoading ? renderLoader(48, 72, 100) : <UserActions logInUrl={loginProps.href} onLoginClick={handleLoginClick} />;

  if (userDetailsAvailable) {
    return isSmallScreenSize ? loggedInMobileView() : loggedInDesktopView();
  }

  return isSmallScreenSize ? loggedOutMobileView() : loggedOutDesktopView();
};
